import React, { useState, useEffect } from 'react';
import CustomSelect from './CustomSelect';

interface AspectValue {
  localizedValue: string;
  valueConstraints?: Array<{
    applicableForLocalizedAspectName: string;
    applicableForLocalizedAspectValues: string[];
  }>;
}

interface AspectConstraint {
  aspectDataType: 'DATE' | 'NUMBER' | 'STRING' | 'STRING_ARRAY';
  itemToAspectCardinality: 'MULTI' | 'SINGLE';
  aspectMode: 'FREE_TEXT' | 'SELECTION_ONLY';
  aspectRequired: boolean;
  aspectUsage: 'RECOMMENDED' | 'OPTIONAL';
  aspectEnabledForVariations: boolean;
  aspectApplicableTo: string[];
  aspectMaxLength?: number;
  aspectFormat?: string;
}

interface Aspect {
  localizedAspectName: string;
  aspectConstraint: AspectConstraint;
  aspectValues?: AspectValue[];
}

interface AspectFormProps {
  aspects: Aspect[];
  data: Record<string, any>;
  onChange?: (updatedFields: { name: string; value: any }) => void;
}

const AspectForm: React.FC<AspectFormProps> = ({ aspects, data, onChange }) => {
  const [formData, setFormData] = useState<Record<string, any>>(data || {});

  // Update form data when external data changes
  useEffect(() => {
    setFormData(data || {});
  }, [data]);

  const handleInputBlur = (aspectName: string, value: any) => {
    const arrayValue = Array.isArray(value) ? value : [value].filter(v => v !== '');
    // Call onChange with the updated field
    if (onChange) {
      onChange({ name: aspectName, value: arrayValue });
    }
  };

  const handleInputChange = (aspectName: string, value: any) => {
    // Convert value to array if it's not already an array
    const arrayValue = Array.isArray(value) ? value : [value].filter(v => v !== '');
    
    const newFormData = {
      ...formData,
      [aspectName]: arrayValue,
    };

    if (arrayValue.length === 0) {
      delete newFormData[aspectName];
    }
    
    setFormData(newFormData);
  };

  const renderField = (aspect: Aspect) => {
    const {
      localizedAspectName,
      aspectConstraint,
      aspectValues,
    } = aspect;

    const {
      aspectDataType,
      aspectMode,
      aspectRequired,
      itemToAspectCardinality,
    } = aspectConstraint;

    // Convert aspectValues to options format for CustomSelect
    const options = aspectValues?.map(av => ({
      value: av.localizedValue,
      label: av.localizedValue,
    })) || [];

    // Get the current value as array and handle display for single/multi
    const currentValue = formData[localizedAspectName] || [];
    const displayValue = itemToAspectCardinality === 'SINGLE' ? 
      (currentValue[0] || '') : 
      currentValue;

    // Handle cases where we need to use CustomSelect
    if (aspectValues && (aspectMode === 'SELECTION_ONLY' || aspectMode === 'FREE_TEXT')) {
      return (
        <div key={localizedAspectName} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {localizedAspectName}
            {aspectRequired && <span className="text-red-500">*</span>}
          </label>
          <CustomSelect
            options={options}
            value={displayValue}
            onChange={(value) => handleInputChange(localizedAspectName, value)}
            onConfirm={(value) => handleInputBlur(localizedAspectName, value)}
            placeholder={`Select ${localizedAspectName}`}
            multiple={itemToAspectCardinality === 'MULTI'}
            allowCustomValue={aspectMode === 'FREE_TEXT'}
            required={aspectRequired}
          />
        </div>
      );
    }

    // Handle free text inputs based on data type
    let inputType = 'text';
    if (aspectDataType === 'NUMBER') {
      inputType = 'number';
    } else if (aspectDataType === 'DATE') {
      inputType = 'date';
    }

    return (
      <div key={localizedAspectName} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {localizedAspectName}
          {aspectRequired && <span className="text-red-500">*</span>}
        </label>
        <input
          type={inputType}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-blue-500 focus:ring-blue-500"
          value={displayValue}
          onChange={(e) => handleInputChange(localizedAspectName, e.target.value)}
          onBlur={(e) => handleInputBlur(localizedAspectName, e.target.value)}
          required={aspectRequired}
        />
      </div>
    );
  };

  return (
    <form className="space-y-6">
      {aspects.map((aspect) => renderField(aspect))}
      <div className="flex justify-end">
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Submit
        </button>
      </div>
    </form>
  );
};

export default AspectForm; 